import QuestionnaireService from "@/services/api/questionnaire";
import type { GetQuestionParams, GetQuestionResponse, PostQuestionRequest, PostQuestionResponse, QuestionField } from "@/types/question";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import type { PayloadAction } from "@reduxjs/toolkit";

interface QuestionState {
  questions: QuestionField[];
  totalQuestions: number;
  isFetching: boolean;
  isPosting: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
}

const name = "question";

const initialState: QuestionState = {
  questions: [],
  totalQuestions: 0,
  isFetching: false,
  isPosting: false,
  isUpdating: false,
  isDeleting: false,
};

export const getQuestions = createAsyncThunk<
  GetQuestionResponse,
  GetQuestionParams,
  { rejectValue: string }
>(`${name}/getQuestions`, async (params, { rejectWithValue }) => {
  try {
    const data: GetQuestionResponse =
      await QuestionnaireService.getAllQuestions(params);
    return data ?? { results: [], totalRecord: 0 };
  } catch (err) {
    const error = err as { message?: string };
    return rejectWithValue(error.message ?? "Failed to fetch questions");
  }
});

export const postQuestion = createAsyncThunk<
  PostQuestionResponse,
  PostQuestionRequest,
  { rejectValue: string }
>(`${name}/postQuestion`, async (params, { rejectWithValue }) => {
  try {
    const data: PostQuestionResponse =
      await QuestionnaireService.postQuestion(params);
    return data ?? {};
  } catch (err) {
    const error = err as { message?: string };
    return rejectWithValue(error.message ?? "Failed to post question");
  }
});

export const updateQuestion = createAsyncThunk<
  PostQuestionResponse,
  { id: string; data: PostQuestionRequest },
  { rejectValue: string }
>(`${name}/updateQuestion`, async (params, { rejectWithValue }) => {
  try {
    const data: PostQuestionResponse = await QuestionnaireService.updateQuestion(
      params.id,
      params.data,
    );
    return data ?? {};
  } catch (err) {
    const error = err as { message?: string };
    return rejectWithValue(error.message ?? "Failed to update question");
  }
});

export const deleteQuestion = createAsyncThunk<
  void,
  string,
  { rejectValue: string }
>(`${name}/deleteQuestion`, async (id, { rejectWithValue }) => {
  try {
    await QuestionnaireService.deleteQuestion(id);
  } catch (err) {
    const error = err as { message?: string };
    return rejectWithValue(error.message ?? "Failed to delete question");
  }
});

export const updateQuestionsOrder = createAsyncThunk<
  void,
  { items: any[] },
  { rejectValue: string }
>(`${name}/updateQuestionsOrder`, async (params, { rejectWithValue }) => {
  try {
    const promises = params.items.map((it) =>
      QuestionnaireService.updateQuestionPartial(it.id, {
        displayOrder: it.displayOrder,
        fieldType: it.fieldType,
        userType: it.userType,
        label: it.label,
      }),
    );
    await Promise.all(promises);
  } catch (err) {
    const error = err as { message?: string };
    return rejectWithValue(error.message ?? "Failed to update question order");
  }
});

const questionSlice = createSlice({
  name: name,
  initialState,
  reducers: {
    clear: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getQuestions.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(
        getQuestions.fulfilled,
        (state, action) => {
          state.isFetching = false;
          if (action.meta.arg.pageNo > 1) {
            const existingIds = new Set(state.questions.map((question) => question.id));
            const nextResults = action.payload.results.filter(
              (question) => !existingIds.has(question.id),
            );

            state.questions = [...state.questions, ...nextResults];
          } else {
            state.questions = action.payload.results;
          }
          state.totalQuestions = action.payload.totalRecord;
        },
      )
      .addCase(getQuestions.rejected, (state, action) => {
        state.isFetching = false;
        console.error("Failed to fetch questions:", action.payload);
      })
      .addCase(postQuestion.pending, (state) => {
        state.isPosting = true;
      })
      .addCase(
        postQuestion.fulfilled,
        (state, action: PayloadAction<PostQuestionResponse>) => {
          state.isPosting = false;
          console.log("Question posted successfully:", action.payload);
        },
      )
      .addCase(postQuestion.rejected, (state, action) => {
        state.isPosting = false;
        console.error("Failed to post question:", action.payload);
      })
      .addCase(updateQuestion.pending, (state) => {
        state.isUpdating = true;
      })
      .addCase(
        updateQuestion.fulfilled,
        (state, action: PayloadAction<PostQuestionResponse>) => {
          state.isUpdating = false;
          console.log("Question updated successfully:", action.payload);
        },
      )
      .addCase(updateQuestion.rejected, (state, action) => {
        state.isUpdating = false;
        console.error("Failed to update question:", action.payload);
      })
      .addCase(deleteQuestion.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deleteQuestion.fulfilled, (state) => {
        state.isDeleting = false;
        console.log("Question deleted successfully");
      })
      .addCase(deleteQuestion.rejected, (state, action) => {
        state.isDeleting = false;
        console.error("Failed to delete question:", action.payload);
      });

    builder
      .addCase(updateQuestionsOrder.pending, (state) => {
      })
      .addCase(updateQuestionsOrder.fulfilled, (state) => {
      })
      .addCase(updateQuestionsOrder.rejected, (state, action) => {
        console.error("Failed to update questions order:", action.payload);
      });
  },
});

export default questionSlice.reducer;
export const { clear } = questionSlice.actions;
export type { QuestionState };
